import { MutableRefObject, useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import * as THREE from 'three';
import { CameraControls } from '@react-three/drei';
import { projectionModeState } from '../state/viewModeAtoms';
import {
  calcOrthoZoomFromPerspective,
  calcPerspectiveDistanceFromOrtho,
} from '../utils/viewModeSync';

interface Params {
  cameraRef: MutableRefObject<CameraControls | null>;
  perspCam: THREE.PerspectiveCamera | null;
  orthoCam: THREE.OrthographicCamera | null;
  canvasHeight: number;
}

// projectionMode 切替時に「画面上の見た目サイズ」を維持しつつ
// active カメラを 2 つの間で同期する hook。
export const useViewModeSync = ({
  cameraRef,
  perspCam,
  orthoCam,
  canvasHeight,
}: Params): void => {
  const mode = useRecoilValue(projectionModeState);
  const prevMode = useRef(mode);

  useEffect(() => {
    if (prevMode.current === mode) return;
    if (!cameraRef.current) {
      prevMode.current = mode;
      return;
    }
    if (!perspCam || !orthoCam) {
      prevMode.current = mode;
      return;
    }

    const target = new THREE.Vector3();
    const position = new THREE.Vector3();
    cameraRef.current.getTarget(target, true);
    cameraRef.current.getPosition(position, true);
    const distance = position.distanceTo(target);

    if (mode === 'orthographic') {
      // persp → ortho: visibleHeight 維持で zoom 算出
      const zoom = calcOrthoZoomFromPerspective(perspCam.fov, distance, canvasHeight);
      orthoCam.zoom = zoom;
      orthoCam.position.copy(position);
      orthoCam.up.copy(perspCam.up);
      orthoCam.updateProjectionMatrix();
    } else {
      // ortho → persp: zoom から逆算した distance に position を再配置
      const desiredDistance = calcPerspectiveDistanceFromOrtho(
        perspCam.fov,
        orthoCam.zoom,
        canvasHeight,
      );
      const dir = new THREE.Vector3().subVectors(position, target).normalize();
      perspCam.position.copy(target).add(dir.multiplyScalar(desiredDistance));
      perspCam.up.copy(orthoCam.up);
      perspCam.updateProjectionMatrix();
    }

    prevMode.current = mode;
  }, [mode, cameraRef, perspCam, orthoCam, canvasHeight]);
};
